/*
 * Copyright (c) 2023, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';

import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';

import { getFormatteddDateTime, renderSkeleton } from '../../../../data/utils';
import { Api, ApiReducer, ApplicationState, SubscriptionsReducer, UserReducer } from '../../../../data/interfaces';
import {
    fetchAPIVersionRangeSubscriptions,
    fetchAllApisOfMajorVersionAuthenticated
} from '../../../../data/operations';
import { Accordion, AccordionDetails, AccordionSummary, List, ListItem, ListItemText } from '@material-ui/core';
import { Link } from 'react-router-dom';
import { DEFAULT_APP_INTERNAL_PREFIX, PATH_SEPARATOR, RESOURCE_API, RESOURCE_APPLICATIONS, RESOURCE_OVERVIEW, SUBSCRIBE_SCOPE } from '../../../../data/constants';
import useStyles from './styles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import classNames from 'classnames';

interface AvailableVersionsProps {
    apiReducer: ApiReducer,
    userReducer: UserReducer,
    subscriptionInfo: SubscriptionsReducer,
    dispatchFetchAllApisOfMajorVersionAuthenticated: (
        organizationId: string,
        apiName: string,
        apiMajorVersion: string
    ) => Promise<void>;
    dispatchFetchVersionRangeSubscriptions: (apiId: string, organizationId: string) => void;
}

interface AppSub {
    appName: string,
    appUuid: string,
    createdTime: string,
}

interface SubscribedVersion {
    version: string,
    apiUuid: string,
    subscriptionList: AppSub[],
}

function LatestVersionListItem(props: {
    latestVersion: string,
    latestVersionUuid: string,
    selectedApiVersion: string,
    orgHandle: string
}) {
    const { latestVersion, latestVersionUuid, selectedApiVersion, orgHandle } = props;
    const classes = useStyles();
    return (
        <ListItem>
            {latestVersion === selectedApiVersion ? (
                <ListItemText>
                    <Typography className={classes.boldText}>
                        <FormattedMessage
                            id="api.apiOverview.allVersions.latestVersion"
                            defaultMessage="{latestVersion} - Latest Version"
                            values={{ latestVersion }}
                        />
                    </Typography>
                </ListItemText>
            ) : (
                <ListItemText>
                    <Typography className={classes.smallText}>
                        <Link className={classes.link} to={PATH_SEPARATOR + orgHandle + RESOURCE_API
                            + PATH_SEPARATOR + latestVersionUuid + RESOURCE_OVERVIEW}>
                            <FormattedMessage
                                id="api.apiOverview.allVersions.latestVersion"
                                defaultMessage="{latestVersion} - Latest Version"
                                values={{ latestVersion }}
                            />
                        </Link>
                    </Typography>
                </ListItemText>
            )}
        </ListItem>
    );
}

function SubscribedVersionsListItem(props: {
    subVersions: SubscribedVersion[],
    selectedApiVersion: string,
    orgHandle: string
}) {
    const { subVersions, selectedApiVersion, orgHandle } = props;
    const classes = useStyles();

    return (
        <ListItem>
            <div>
                <ListItemText>
                    <Typography className={classes.smallText}>
                        <FormattedMessage
                            id="api.apiOverview.allVersions.subscribedVersions"
                            defaultMessage="Subscribed Versions"
                        />
                    </Typography>
                </ListItemText>
                <div>
                    {subVersions.map(subVersion => (
                        <ExpandableSubscribedVersionNestedListItem
                            key={subVersion.apiUuid}
                            subVersion={subVersion}
                            selectedApiVersion={selectedApiVersion}
                            orgHandle={orgHandle}
                        />
                    ))}
                </div>
            </div>
        </ListItem>
    );
}

function ExpandableSubscribedVersionNestedListItem(props: {
    subVersion: SubscribedVersion,
    selectedApiVersion: string,
    orgHandle: string
}) {
    const classes = useStyles();
    const { subVersion, selectedApiVersion, orgHandle } = props;

    const [expandedPanel, setExpandedPanel] = useState<string>();

    const handlePanelChange = (panelId: string) => (_: any, isExpanded: boolean) => {
        setExpandedPanel(isExpanded ? panelId : "");
    };

    return (
        <Accordion
            key={subVersion.apiUuid}
            expanded={expandedPanel === subVersion.apiUuid}
            onChange={handlePanelChange(subVersion.apiUuid)}
            className={classNames(classes.customAccordionSummaryRoot, classes.customAccordionDetails)}
        >
            <AccordionSummary className={classes.customAccordionSummaryContent} expandIcon={<ExpandMoreIcon />}>
                {selectedApiVersion == subVersion.version ? (
                    <Typography className={classes.boldText}>
                        {subVersion.version}
                    </Typography>
                ) : (
                    <div onClick={event => event.stopPropagation()}>
                        <Typography className={classes.smallText}>
                            <Link
                                className={classes.link}
                                to={PATH_SEPARATOR + orgHandle + RESOURCE_API + PATH_SEPARATOR + subVersion.apiUuid
                                    + RESOURCE_OVERVIEW}
                            >
                                {subVersion.version}
                            </Link>
                        </Typography>
                    </div>
                )}
            </AccordionSummary>
            <AccordionDetails>
                <List dense={true} className={classes.customDenseListItem}>
                    {subVersion.subscriptionList
                        .filter(
                            sub => !sub.appName.startsWith(DEFAULT_APP_INTERNAL_PREFIX))
                        .map(sub => (
                            <ListItem key={sub.appUuid}>
                                <ListItemText className={classes.smallText}>
                                    <Typography className={classes.smallText}>
                                        <FormattedMessage
                                            id="api.apiOverview.allVersions.subscribedApp"
                                            defaultMessage="{appName} - Created on - {createdTime}"
                                            values={{
                                                appName: (
                                                    <Link className={classes.link} to={
                                                        PATH_SEPARATOR + orgHandle + RESOURCE_APPLICATIONS
                                                        + PATH_SEPARATOR + sub.appUuid
                                                        + RESOURCE_OVERVIEW}
                                                    >
                                                        {sub.appName}
                                                    </Link>
                                                ),
                                                createdTime: getFormatteddDateTime(sub.createdTime)
                                            }}
                                        />

                                    </Typography>
                                </ListItemText>
                            </ListItem>
                        ))}
                </List>
            </AccordionDetails>
        </Accordion>
    );
}

function AllVersionList(props: {
    latestVersion: string,
    latestVersionUuid: string,
    selectedApiVersion: string,
    orgHandle: string,
    subVersions: SubscribedVersion[]
}) {
    const { latestVersion, latestVersionUuid, selectedApiVersion, orgHandle, subVersions } = props;
    const classes = useStyles();

    return (
        <List dense={true} className={classes.customDenseListItem}>
            <LatestVersionListItem
                latestVersion={latestVersion}
                selectedApiVersion={selectedApiVersion}
                latestVersionUuid={latestVersionUuid}
                orgHandle={orgHandle}
            />
            <SubscribedVersionsListItem
                subVersions={subVersions}
                orgHandle={orgHandle}
                selectedApiVersion={selectedApiVersion}
            />
        </List>
    );
}

function AvailableVersions(props: AvailableVersionsProps) {
    const {
        apiReducer,
        userReducer,
        subscriptionInfo,
        dispatchFetchAllApisOfMajorVersionAuthenticated,
        dispatchFetchVersionRangeSubscriptions,
    } = props;
    const { selectedOrg, user } = userReducer;
    const classes = useStyles();
    const { majorVersionApiList, majorVersionApisLoading, api, loading: apiLoading } = apiReducer;
    const { subscriptions, isLoading: isSubscriptionFetching } = subscriptionInfo;
    const [subscribedVersions, setSubscribedVersions] = useState<SubscribedVersion[]>([]);
    const loading = majorVersionApisLoading || isSubscriptionFetching;

    const sortedApiList = majorVersionApiList.list && majorVersionApiList.list
        .filter((a: Api) => a.name === api.name)
        .sort((a: Api, b: Api) => {
            if (a.version > b.version) {
                return -1;
            }
            if (a.version < b.version) {
                return 1;
            }
            return 0;
        });
    const latestApi = sortedApiList && sortedApiList.length > 0 && sortedApiList[0];
    const otherApis = sortedApiList && sortedApiList.length > 1 ? sortedApiList.slice(1) : [];

    useEffect(() => {
        if (selectedOrg.uuid && !apiLoading) {
          dispatchFetchAllApisOfMajorVersionAuthenticated(
            selectedOrg.uuid,
            api.name,
            api.version.split(".")[0]
          );
          if (user.allowedScopes.includes(SUBSCRIBE_SCOPE)) { 
            dispatchFetchVersionRangeSubscriptions(api.id, selectedOrg.uuid);
          }
        }
    }, [apiLoading]);

    useEffect(() => {
        if (!isSubscriptionFetching && !majorVersionApisLoading && subscriptions && otherApis) {
            const subVersions: SubscribedVersion[] = [];
            otherApis.forEach((api: Api) => {
                const subscriptionsOfApi = subscriptions.list.filter((sub) => sub.apiId === api.id).map((sub) => {
                    return {
                        appName: sub.applicationInfo?.name || '',
                        appUuid: sub.applicationId,
                        createdTime: sub.createdTime || '',
                    };
                });
                if (subscriptionsOfApi.length > 0) {
                    subVersions.push({
                        version: api.version,
                        apiUuid: api.id,
                        subscriptionList: subscriptionsOfApi,
                    });
                }
            });
            setSubscribedVersions(subVersions);
        }
    }, [subscriptions, majorVersionApiList]);

    if (!loading && (!latestApi || subscribedVersions.length === 0)) {
        return null;
    }

    return (
        <Grid container spacing={2}>
            {loading ? renderSkeleton("rect", "50%", 25, { className: classes.title }) : (
                <Grid item xs={12} sm container className={classes.title}>
                    <Grid item xs lg={12} className={classes.grid}>
                        <Typography className={classes.titleHead}>
                            <FormattedMessage
                                id="api.apiOverview.availableVersions.title"
                                defaultMessage="Available Versions - {apiMajorVersion}"
                                values={{ apiMajorVersion: api.version.split(".")[0] }}
                            />
                        </Typography>
                    </Grid>
                </Grid>
            )}
            <Grid item xs={12} sm container lg={12}>
                {loading ? (
                    <>
                        {renderSkeleton("rect", "50%", 25, { className: classes.indentedSkeleton })}
                        <div className={classes.skeletonSpacer} />
                        {renderSkeleton("rect", "50%", 25, {
                            className: classNames(classes.skeletonSpacer, classes.indentedSkeleton)
                        })}
                    </>) : (
                    <AllVersionList
                        subVersions={subscribedVersions}
                        latestVersion={latestApi && latestApi.version || ''}
                        latestVersionUuid={latestApi && latestApi.id || ''}
                        selectedApiVersion={api.version}
                        orgHandle={selectedOrg.handle}
                    />
                )}
            </Grid>
        </Grid>
    )
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        apiReducer: state.apiReducer,
        userReducer: state.userReducer,
        subscriptionInfo: state.subscriptionsReducer,
    }
}

const mapDispatchToProps = {
    dispatchFetchAllApisOfMajorVersionAuthenticated: fetchAllApisOfMajorVersionAuthenticated,
    dispatchFetchVersionRangeSubscriptions: fetchAPIVersionRangeSubscriptions,
};

export default connect(mapStateToProps, mapDispatchToProps)(AvailableVersions);
